import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const prepareHeaders = (headers) => {
  const token = localStorage.getItem("accessToken");
  if (token) headers.set("authorization", `Bearer ${token}`);
  return headers;
};

// Resolve base URL at call time (handles when window.baseDomain is set later)
const resolveBaseUrl = () => {
  // window at runtime
  if (typeof window !== "undefined" && window.baseDomain) {
    return window.baseDomain; // must end with a trailing slash
  }
  // Fallbacks (env vars / default)
  // Vite: import.meta.env.VITE_API_BASE; CRA: process.env.REACT_APP_API_BASE
  const envUrl =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
    (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE) ||
    "http://localhost:5050/";
  return envUrl.endsWith("/") ? envUrl : envUrl + "/";
};

// Helper: wait ms
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// A wrapper that re-creates fetchBaseQuery per request so it picks up the latest base.
// Includes automatic retry with back-off for 429 (Too Many Requests) responses.
export const dynamicBaseQuery = async (args, api, extraOptions) => {
  const baseUrl = resolveBaseUrl();
  const inner = fetchBaseQuery({
    baseUrl,
    credentials: "include",
    prepareHeaders,
  });

  const MAX_RETRIES = 3;

  let result;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    result = await inner(args, api, extraOptions);

    if (result.error?.status === 429 && attempt < MAX_RETRIES) {
      // Use Retry-After header if present, otherwise exponential back-off
      const retryAfter = result.meta?.response?.headers?.get("Retry-After");
      const delayMs = retryAfter
        ? parseInt(retryAfter, 10) * 1000
        : Math.min(1000 * 2 ** attempt, 10000);
      await wait(delayMs);
      continue;
    }

    return result;
  }

  // All retries exhausted — return the last result (429)
  return result;
};
